import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Send, Twitter, Facebook, Linkedin, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { StoreBadges } from '@/components/StoreBadges';
import { FOOTER } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_URL = 'https://copy.click';

export const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const shareText = t('footer.shareText');
  const SHARES = [
    { icon: Send, label: 'Telegram', href: `https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(shareText)}` },
    { icon: Twitter, label: 'X', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(shareText)}` },
    { icon: Facebook, label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}` },
    { icon: Linkedin, label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}` },
  ];

  const COLS = [
    {
      title: t('footer.colProduct'),
      links: [
        { label: t('footer.linkFollowers'), to: '/followers' },
        { label: t('footer.linkProviders'), to: '/providers' },
        { label: t('footer.linkPricing'), to: '/pricing' },
        { label: t('footer.linkHow'), to: '/#how-it-works' },
      ],
    },
    {
      title: t('footer.colResources'),
      links: [
        { label: t('footer.linkGuides'), to: '/guides' },
        { label: t('footer.linkFaq'), to: '/faq' },
        { label: t('footer.linkContact'), to: '/contact' },
      ],
    },
    {
      title: t('footer.colLegal'),
      links: [
        { label: t('footer.linkPrivacy'), to: '/legal/privacy' },
        { label: t('footer.linkTerms'), to: '/legal/terms' },
        { label: t('footer.linkCookies'), to: '/legal/cookies' },
        { label: t('footer.linkEula'), to: '/legal/eula' },
      ],
    },
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success(t('footer.subscribeSuccess'));
      setEmail('');
    } catch {
      toast.error(t('footer.subscribeError'));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    const legacyCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = SHARE_URL;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (!ok) throw new Error('copy failed');
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(SHARE_URL);
      } else {
        legacyCopy();
      }
      toast.success(t('footer.linkCopied'));
    } catch {
      try {
        legacyCopy();
        toast.success(t('footer.linkCopied'));
      } catch {
        toast.error(t('footer.linkCopyError'));
      }
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />
      <div className="pointer-events-none absolute -top-28 left-1/2 h-56 w-[680px] -translate-x-1/2 rounded-full bg-brand/10 blur-[130px]" />

      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 flex select-none justify-center overflow-hidden">
        <span className="translate-y-[38%] font-display text-[15vw] font-extrabold leading-none text-white/[0.028]">copy.click</span>
      </div>

      <div className="relative mx-auto max-w-6xl px-6 pt-16 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-6 rounded-3xl border border-white/10 bg-[#001826]/70 p-7 text-center backdrop-blur-sm sm:p-9 lg:flex-row lg:items-center lg:text-left">
          <div className="max-w-md">
            <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{t('footer.newsletterTitle')}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/55">{t('footer.newsletterSubtitle')}</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md items-center gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.emailPlaceholder')}
              data-testid={FOOTER.newsletterEmail}
              className="h-12 w-full min-w-0 flex-1 rounded-full border border-white/10 bg-elevated px-5 text-sm text-white placeholder:text-white/40 transition-colors focus:border-brand focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid={FOOTER.newsletterSubmit}
              className="inline-flex h-12 shrink-0 items-center gap-2 rounded-full bg-brand px-5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50"
            >
              {loading ? t('footer.joining') : t('footer.subscribe')} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div className="flex flex-col items-center space-y-5 text-center lg:items-start lg:text-left">
            <Logo />
            <p className="max-w-xs text-sm leading-relaxed text-white/50">
              {t('footer.description')}
            </p>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-white/40">{t('footer.shareTitle')}</p>
              <div className="flex items-center justify-center gap-3 lg:justify-start">
                {SHARES.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Share on ${s.label}`}
                    data-testid={`footer-share-${s.label.toLowerCase()}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/55 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:text-brand"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
                <button
                  type="button"
                  onClick={copyLink}
                  aria-label="Copy link"
                  data-testid="footer-share-copy"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/55 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:text-brand"
                >
                  <Link2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <StoreBadges />
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="text-center lg:text-left">
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-widest text-white/40">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="group inline-flex items-center text-sm text-white/70 transition-colors hover:text-brand">
                      <span className="h-px w-0 bg-brand transition-all duration-300 group-hover:mr-2 group-hover:w-3" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 py-6 text-xs text-white/40 sm:flex-row">
          <p className="text-center sm:text-left">© {new Date().getFullYear()} Copy.Click. {t('footer.rights')}</p>
          <p className="text-center sm:text-right">{t('footer.disclaimer')}</p>
        </div>
      </div>
    </footer>
  );
};
